import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const STORAGE_KEY = "philolms_patron_dashboard_tour_v1";

const STEPS: { selector: string; title: string; body: string }[] = [
  {
    selector: '[data-patron-tour="welcome"]',
    title: "Your library home",
    body: "See a snapshot of your account, quick links, and anything that needs attention.",
  },
  {
    selector: '[data-patron-tour="library-card"]',
    title: "Library card",
    body: "Your virtual card and physical card status live here. You will get an email when your card is ready for pickup.",
  },
  {
    selector: '[data-patron-tour="stats"]',
    title: "Loans, invoices, and fines",
    body: "Tap a metric to open checkouts, invoices, or fines. Pending library fees appear under Invoices.",
  },
  {
    selector: '[data-patron-tour="checkouts"]',
    title: "Reading activity",
    body: "Current loans and shortcuts to the catalog are just below. Use Search Catalog anytime to find materials.",
  },
];

/**
 * One-time dismissible tour for patrons on the main dashboard. No extra dependencies.
 */
export function PatronOnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (typeof window !== "undefined" && !window.localStorage.getItem(STORAGE_KEY)) {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  const dismiss = useCallback((markDone: boolean) => {
    if (markDone) {
      try {
        window.localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    document.querySelectorAll("[data-patron-tour-active]").forEach((el) => {
      el.removeAttribute("data-patron-tour-active");
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const sel = STEPS[step]?.selector;
    if (!sel) return;
    document.querySelectorAll("[data-patron-tour-active]").forEach((el) => {
      el.removeAttribute("data-patron-tour-active");
    });
    const el = document.querySelector(sel);
    if (el) {
      el.setAttribute("data-patron-tour-active", "true");
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [open, step]);

  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const isLast = step >= STEPS.length - 1;
  const current = STEPS[step];

  return (
    <>
      <div
        className="fixed inset-0 z-[200] bg-background/60 backdrop-blur-[2px]"
        aria-hidden
        onClick={() => dismiss(true)}
      />
      <div className="fixed bottom-4 left-4 right-4 z-[210] mx-auto max-w-md sm:left-auto sm:right-6 sm:mx-0">
        <div
          className="rounded-xl border border-border bg-card p-4 shadow-lg"
          role="dialog"
          aria-labelledby="patron-tour-title"
          aria-describedby="patron-tour-desc"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-medium text-muted-foreground">
              Tip {step + 1} of {STEPS.length}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => dismiss(true)}
              aria-label="Close tour"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <h2
            id="patron-tour-title"
            className="mt-1 font-heading text-lg font-semibold text-foreground"
          >
            {current.title}
          </h2>
          <p id="patron-tour-desc" className="mt-2 text-sm text-muted-foreground">
            {current.body}
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => dismiss(true)}>
              Don&apos;t show again
            </Button>
            {!isLast ? (
              <Button type="button" size="sm" onClick={() => setStep((s) => s + 1)}>
                Next
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={() => dismiss(true)}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
      <style>{`
        [data-patron-tour-active="true"] {
          position: relative;
          z-index: 205;
          outline: 3px solid hsl(var(--primary));
          outline-offset: 4px;
          border-radius: 12px;
          transition: outline-offset 0.2s ease;
        }
      `}</style>
    </>
  );
}
